import {
	Column,
	CreateDateColumn,
	DeleteDateColumn,
	JoinColumn,
	ManyToOne,
	PrimaryGeneratedColumn,
	UpdateDateColumn,
} from 'typeorm';
import type { UserEntity } from './user.entity';

/**
 * Shared audit columns and relations, WITHOUT the `_id` primary key. The PK
 * lives on the two sibling subclasses below (`BaseEntity` / `BigIntBaseEntity`)
 * because TypeORM cannot override an inherited column's type: metadata
 * collection keeps the FIRST-registered args per property name, and a base
 * class always registers before its subclasses. The old pattern of
 * re-declaring `id` with `@PrimaryGeneratedColumn({ type: 'bigint' })` in a
 * child entity was silently ignored — the column resolved as `int`, and any
 * FK join column pointing at it was forced to `int` as well (TypeORM
 * overwrites a join column's declared type with the referenced PK's type).
 * That is what broke database sync for `document_email_send` →
 * `email_record`. Keeping exactly one `id` declaration per inheritance tree
 * is the fix.
 */
export abstract class AbstractBaseEntity<TIdType = number> {
	public constructor(data?: Partial<AbstractBaseEntity<TIdType>>) {
		if (data == null) {
			return;
		}

		if (data.id != null) {
			this.id = data.id;
		}

		if (data.createdDate != null) {
			this.createdDate = data.createdDate;
		}

		if (data.modifiedDate != null) {
			this.modifiedDate = data.modifiedDate;
		}

		if (data.deletedDate != null) {
			this.deletedDate = data.deletedDate;
		}

		if (data.ownerUserId != null) {
			this.ownerUserId = data.ownerUserId;
		}

		if (data.creatorUserId != null) {
			this.creatorUserId = data.creatorUserId;
		}

		if (data.modifiedUserId != null) {
			this.modifiedUserId = data.modifiedUserId;
		}

		if (data.deletedUserId != null) {
			this.deletedUserId = data.deletedUserId;
		}
	}

	public id!: TIdType;

	@Column({
		type: 'int',
		name: '_owner_userId',
		nullable: true,
		unsigned: true,
	})
	public ownerUserId?: number;

	@CreateDateColumn({
		name: '_created_date',
		nullable: false,
		precision: 0,
		default: () => 'CURRENT_TIMESTAMP',
	})
	public createdDate!: Date;

	@Column({
		type: 'int',
		name: '_creator_userId',
		nullable: true,
		unsigned: true,
	})
	public creatorUserId?: number;

	@UpdateDateColumn({
		name: '_modified_date',
		nullable: false,
		precision: 0,
		default: () => 'CURRENT_TIMESTAMP',
		onUpdate: 'CURRENT_TIMESTAMP',
	})
	public modifiedDate!: Date;

	@Column({
		type: 'int',
		name: '_modified_userId',
		nullable: true,
		unsigned: true,
	})
	public modifiedUserId?: number;

	@DeleteDateColumn({ name: '_deleted_date', precision: 0, nullable: true })
	public deletedDate?: Date | null;

	@Column({
		type: 'int',
		name: '_deleted_userId',
		nullable: true,
		unsigned: true,
	})
	public deletedUserId?: number | null;

	// String-based reference avoids circular import (UserEntity extends BaseEntity)
	// FK references _workd__user__id, not the user table's _id primary key
	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([{ name: '_owner_userId', referencedColumnName: 'workdUserId' }])
	public owner?: UserEntity | null;

	// String-based reference avoids circular import (UserEntity extends BaseEntity)
	// FK references _workd__user__id, not the user table's _id primary key
	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: '_creator_userId', referencedColumnName: 'workdUserId' },
	])
	public creator?: UserEntity | null;

	// String-based reference avoids circular import (UserEntity extends BaseEntity)
	// FK references _workd__user__id, not the user table's _id primary key
	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: '_modified_userId', referencedColumnName: 'workdUserId' },
	])
	public modifier?: UserEntity | null;

	// String-based reference avoids circular import (UserEntity extends BaseEntity)
	// FK references _workd__user__id, not the user table's _id primary key
	@ManyToOne('UserEntity', {
		nullable: true,
		createForeignKeyConstraints: false,
	})
	@JoinColumn([
		{ name: '_deleted_userId', referencedColumnName: 'workdUserId' },
	])
	public deleter?: UserEntity | null;
}

/** Standard base: `int unsigned` auto-increment `_id`. */
export abstract class BaseEntity<
	TIdType = number,
> extends AbstractBaseEntity<TIdType> {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	declare public id: TIdType;
}

/**
 * High-volume base: `bigint unsigned` auto-increment `_id`, surfaced as a
 * string (the mysql driver returns bigint values as strings because they can
 * exceed Number.MAX_SAFE_INTEGER). Extend this INSTEAD of `BaseEntity` when a
 * table needs a bigint PK — do NOT re-declare `id` on a `BaseEntity` child;
 * TypeORM ignores the override (see AbstractBaseEntity docs).
 */
export abstract class BigIntBaseEntity extends AbstractBaseEntity<string> {
	@PrimaryGeneratedColumn({ name: '_id', type: 'bigint', unsigned: true })
	declare public id: string;
}

/**
 * Names of the abstract base classes above. Entity-list builders (database
 * sync, crm-api DataSource config, migrations) iterate the Entity namespace
 * and must skip these — they are not tables.
 */
export const ABSTRACT_ENTITY_BASE_CLASS_NAMES: ReadonlySet<string> = new Set([
	AbstractBaseEntity.name,
	BaseEntity.name,
	BigIntBaseEntity.name,
]);
